import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Header } from 'components/Header/Header';
import AuthPage from 'pages/Auth/Auth';
import { useDispatch, useSelector } from 'react-redux';
import { RefreshUser } from 'redux/auth/authOperations';
import { ModalPassRecovery } from 'components/Modal/ModalPassRecovery/ModalPassRecovery';
import { RestrictedRoute } from './RestrictedRoute';
import { PrivateRoute } from './PrivateRoute';
import { HasRunProvider } from './Modal/ModalDebt/ModalOverduePayment';
import Page404 from 'pages/Page404/Page404';
import Footer from './Footer/Footer';
import OAuthCallback from './OAuthCallback/OAuthCallback';
import {
  Calendar as CalendarIcon,
  Home as HomeIcon,
  PieChart,
  Settings,
  Wallet,
} from 'lucide-react';
import { selectIsLoggedIn } from 'redux/selectors';
import { ThemeProvider } from 'styled-components';
import { darkTheme, theme } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { useLanguageSync } from 'hooks/useLanguageSync';

const Home = lazy(() => import('pages/Home/Home'));
const Reports = lazy(() => import('pages/Reports/Reports'));
const WalletsPage = lazy(() => import('pages/Wallet/Wallets'));
const Planning = lazy(() => import('pages/Planning/Planning'));
const BurgerAccount = lazy(() => import('./Header/BurgerAccount/BurgerAcount'));
const SupportPage = lazy(() => import('pages/Support/Support'));


export const App = () => {
  const [date, setDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([null, null]);
  const [title, setTitle] = useState(1);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem('fintrack-theme') === 'dark'
  );

  const [emailForResend, setEmailForResend] = useState('');

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { t } = useTranslation();
  const loc = useLocation();
  useLanguageSync();

  useEffect(() => {
    dispatch(RefreshUser());
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem('path', loc.pathname);
  }, [loc.pathname]);

  useEffect(() => {
    localStorage.setItem('fintrack-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  return (
    <ThemeProvider theme={isDark ? darkTheme : theme}>
      <HasRunProvider>
        <div
          className={`flex min-h-screen flex-col ${
            isDark ? 'theme-dark bg-slate-950 text-slate-100' : 'bg-[#F8F9FA] text-slate-800'
          }`}
        >
        {isLoggedIn && (
          <aside
            className={`fixed left-0 top-0 hidden h-screen w-64 border-r px-5 py-8 md:flex md:flex-col ${
              isDark
                ? 'border-slate-800 bg-slate-900/95'
                : 'border-slate-200 bg-white'
            }`}
          >
            <Link to="/home" className="mb-10 block">
              <p className="font-brand text-3xl text-[#FF7601]">FinTrack</p>
            </Link>
            <nav className="space-y-2">
              {[
                { to: '/home', label: t('pageName.home'), icon: HomeIcon },
                { to: '/reports', label: t('pageName.report'), icon: PieChart },
                { to: '/planning', label: t('pageName.planning'), icon: CalendarIcon },
                { to: '/wallets', label: t('pageName.wallets'), icon: Wallet },
                { to: '/personal-data', label: t('pageName.settings'), icon: Settings },
              ].map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                      isActive
                        ? 'border-[#FF7601] bg-orange-50 text-[#FF7601] shadow-sm'
                        : isDark
                        ? 'border-transparent text-slate-300 hover:border-slate-700 hover:bg-slate-800/70'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-orange-200 hover:bg-orange-50/60'
                    }`
                  }
                >
                  <Icon size={18} />
                  {label}
                </NavLink>
              ))}
            </nav>
          </aside>
        )}
        <Header isDark={isDark} toggleTheme={toggleTheme} />
        <main
          className={`flex-1 px-4 pt-24 md:pb-8 ${
            isLoggedIn
              ? 'max-md:pb-[calc(7rem+env(safe-area-inset-bottom,0px))]'
              : 'pb-8'
          } ${isLoggedIn ? 'md:pl-72' : ''}`}
        >
          <div className="mx-auto w-full max-w-4xl xl:max-w-6xl 2xl:max-w-7xl">
            <Suspense
              fallback={
                <div
                  className={`flex items-center justify-center rounded-3xl border py-20 text-sm font-medium ${
                    isDark
                      ? 'border-slate-700 bg-slate-900 text-slate-400'
                      : 'border-slate-100 bg-white text-slate-500 shadow-sm'
                  }`}
                >
                  {t('common.loading')}
                </div>
              }
            >
              <Routes>
              <Route
                path="/"
                element={
                  <RestrictedRoute
                    component={
                      <AuthPage
                        emailForResend={emailForResend}
                        setEmailForResend={setEmailForResend}
                      />
                    }
                  />
                }
              />
              <Route
                path="/home"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <Home
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        date={date}
                        setDate={setDate}
                        title={title}
                        setTitle={setTitle}
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                      />
                    }
                  />
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <Reports
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        title={title}
                        setTitle={setTitle}
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                      />
                    }
                  />
                }
              />
              <Route
                path="/wallets"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <WalletsPage
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        title={title}
                        setTitle={setTitle}
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                      />
                    }
                  />
                }
              />
              <Route
                path="/planning"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <Planning
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        title={title}
                        setTitle={setTitle}
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                      />
                    }
                  />
                }
              />
              <Route
                path="/personal-data"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <BurgerAccount
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                        isDark={isDark}
                        toggleTheme={toggleTheme}
                      />
                    }
                  />
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <BurgerAccount
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                        isDark={isDark}
                        toggleTheme={toggleTheme}
                      />
                    }
                  />
                }
              />
              <Route
                path="/security"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <BurgerAccount
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                        isDark={isDark}
                        toggleTheme={toggleTheme}
                      />
                    }
                  />
                }
              />
              <Route
                path="/contact-support"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <BurgerAccount
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                        isDark={isDark}
                        toggleTheme={toggleTheme}
                      />
                    }
                  />
                }
              />
              <Route
                path="/donate"
                element={
                  <PrivateRoute
                    redirectTo="/"
                    component={
                      <BurgerAccount
                        setActiveIndex={setActiveIndex}
                        activeIndex={activeIndex}
                        isDark={isDark}
                        toggleTheme={toggleTheme}
                      />
                    }
                  />
                }
              />
              <Route
                path="/auth/modal-password-recovery"
                element={<ModalPassRecovery emailForResend={emailForResend} />}
              />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="*" element={<Page404 />} />
              </Routes>
            </Suspense>
          </div>
        </main>
        <Footer isDark={isDark} />
      </div>
    </HasRunProvider>
    </ThemeProvider>
  );
};
